import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, X, Plus, Check } from 'lucide-react';
import { api, AVAILABLE_FORMATS, AVAILABLE_COLORS, COLOR_HEX } from '../lib/api';
import { useQuoteCart } from '../context/QuoteCartContext';
import { useBuyer } from '../context/BuyerAuthContext';
import ReservedTerms from '../components/ReservedTerms';

function getPName(p, lang) {
  return p.translations?.[lang]?.name || p.translations?.en?.name || p.translations?.it?.name || p.sku;
}
function getPTagline(p, lang) {
  return p.translations?.[lang]?.tagline || p.translations?.en?.tagline || p.translations?.it?.tagline || '';
}

// Per-color CSS filters removed — real per-color pellet photos now available.

export default function CatalogPage() {
  const { t, i18n } = useTranslation();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState('');
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { add, items, setOpen } = useQuoteCart();
  const { buyer } = useBuyer();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    api.get('/products').then(r => setProducts(r.data)).catch(() => {});
  }, []);

  // Auto-open quote drawer if ?quote=1 (e.g. via QR)
  useEffect(() => {
    if (searchParams.get('quote') === '1') setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(() => Array.from(new Set(products.map(p => p.category).filter(Boolean))).sort(), [products]);

  const filtered = useMemo(() => {
    return products.filter(p => {
      if (selectedFormat && !p.formats?.includes(selectedFormat)) return false;
      if (selectedColor && !p.colors?.includes(selectedColor)) return false;
      if (selectedCategory && p.category !== selectedCategory) return false;
      if (q) {
        const term = q.toLowerCase();
        const name = getPName(p, i18n.language).toLowerCase();
        if (!p.sku.toLowerCase().includes(term) && !name.includes(term) && !(p.category || '').toLowerCase().includes(term)) return false;
      }
      return true;
    });
  }, [products, q, selectedFormat, selectedColor, selectedCategory, i18n.language]);

  const inCart = (id) => items.some(i => i.id === id);

  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
      {buyer && buyer.personal_note && (
        <div data-testid="buyer-note-banner" className="mb-12 border-l-2 border-[#C5A059] bg-[#F2ECE0] p-6 md:p-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-2">— Your personal terms · {buyer.company || buyer.name}</div>
          <p className="font-serif italic text-lg text-[#3D2817]/80 whitespace-pre-wrap">{buyer.personal_note}</p>
        </div>
      )}
      <div className="mb-16 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-6">
          <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">— Catalog</div>
          <h1 className="font-serif font-light text-5xl md:text-7xl leading-none">{t('catalog.title')}</h1>
        </div>
        <div className="md:col-span-5 md:col-start-8 flex items-end">
          <p className="font-serif italic text-lg text-[#3D2817]/70">{t('catalog.subtitle')}</p>
        </div>
      </div>

      {/* FILTERS */}
      <div className="mb-16 pb-8 border-b border-[#E5D9C8] space-y-8">
        <div className="relative max-w-md">
          <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#3D2817]/40" />
          <input
            data-testid="catalog-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={t('catalog.search')}
            className="underline-input pl-8"
          />
          {q && (
            <button onClick={() => setQ('')} className="absolute right-0 top-1/2 -translate-y-1/2 text-[#3D2817]/40 hover:text-[#3D2817]">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#3D2817]/60 mb-3">{t('catalog.filter_format')}</div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="filter-format-all"
                onClick={() => setSelectedFormat(null)}
                className={`px-4 py-2 text-xs uppercase tracking-widest border transition-all ${
                  !selectedFormat ? 'bg-[#3D2817] text-white border-[#3D2817]' : 'border-[#E5D9C8] hover:border-[#3D2817]'
                }`}
              >{t('catalog.all')}</button>
              {AVAILABLE_FORMATS.map(f => (
                <button
                  key={f}
                  data-testid={`filter-format-${f}`}
                  onClick={() => setSelectedFormat(selectedFormat === f ? null : f)}
                  className={`px-4 py-2 text-xs uppercase tracking-widest border transition-all ${
                    selectedFormat === f ? 'bg-[#3D2817] text-white border-[#3D2817]' : 'border-[#E5D9C8] hover:border-[#3D2817]'
                  }`}
                >
                  {t(`formats.${f}`)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-[#3D2817]/60 mb-3">{t('catalog.filter_color')}</div>
            <div className="flex flex-wrap gap-3">
              {AVAILABLE_COLORS.map(c => (
                <button
                  key={c}
                  data-testid={`filter-color-${c}`}
                  onClick={() => setSelectedColor(selectedColor === c ? null : c)}
                  title={t(`colors.${c}`)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    selectedColor === c ? 'border-[#C5A059] scale-110' : 'border-[#E5D9C8] hover:border-[#3D2817]/40'
                  }`}
                  style={{ background: COLOR_HEX[c] }}
                />
              ))}
              {selectedColor && (
                <button onClick={() => setSelectedColor(null)} className="text-xs uppercase tracking-widest text-[#3D2817]/50 hover:text-[#3D2817]">
                  clear
                </button>
              )}
            </div>
          </div>

          {categories.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-[#3D2817]/60 mb-3">{t('catalog.filter_category')}</div>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="filter-category-all"
                  onClick={() => setSelectedCategory(null)}
                  className={`px-4 py-2 text-xs uppercase tracking-widest border transition-all ${
                    !selectedCategory ? 'bg-[#3D2817] text-white border-[#3D2817]' : 'border-[#E5D9C8] hover:border-[#3D2817]'
                  }`}
                >{t('catalog.all')}</button>
                {categories.map(c => (
                  <button
                    key={c}
                    data-testid={`filter-category-${c}`}
                    onClick={() => setSelectedCategory(selectedCategory === c ? null : c)}
                    className={`px-4 py-2 text-xs uppercase tracking-widest border transition-all ${
                      selectedCategory === c ? 'bg-[#3D2817] text-white border-[#3D2817]' : 'border-[#E5D9C8] hover:border-[#3D2817]'
                    }`}
                  >{c}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* GRID */}
      {filtered.length === 0 ? (
        <div className="py-24 text-center font-serif italic text-2xl text-[#3D2817]/50">
          {t('catalog.no_results')}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {filtered.map((p, i) => (
            <div
              key={p.id}
              data-testid={`catalog-product-${p.id}`}
              className="group"
              style={{ animation: `fadeUp 0.6s cubic-bezier(0.22,1,0.36,1) ${(i % 6) * 0.08}s both` }}
            >
              <Link to={`/product/${p.id}`} className="block">
                <div className="img-zoom aspect-[3/4] bg-[#F2ECE0] mb-4 relative">
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt={getPName(p, i18n.language)} className="w-full h-full object-cover" data-testid={`product-image-${p.sku}`} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="font-serif text-6xl italic text-[#C5A059]/60">K.</span>
                    </div>
                  )}
                  {p.formats?.length > 0 && (
                    <div className="absolute top-3 left-3 flex gap-1 flex-wrap max-w-[80%]">
                      {p.formats.map(f => (
                        <span key={f} className="bg-white/90 backdrop-blur-sm text-[10px] uppercase tracking-widest px-2 py-1">
                          {t(`formats.${f}`)}
                        </span>
                      ))}
                      {(p.micron_min != null || p.micron_max != null || p.micron) && (
                        <span className="bg-[#C5A059] text-white text-[10px] uppercase tracking-widest px-2 py-1">
                          {(p.micron_min != null && p.micron_max != null)
                            ? `${p.micron_min}–${p.micron_max}µ`
                            : `${p.micron_max ?? p.micron_min ?? p.micron}µ`}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </Link>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-[#3D2817]/50">{p.sku}</div>
                  <Link to={`/product/${p.id}`} className="font-serif text-2xl leading-tight hover:text-[#C5A059] transition-colors">
                    {getPName(p, i18n.language)}
                  </Link>
                  {getPTagline(p, i18n.language) && (
                    <p className="mt-1 text-sm text-[#3D2817]/60 font-serif italic">{getPTagline(p, i18n.language)}</p>
                  )}
                  {p.colors?.length > 0 && (
                    <div className="mt-3 flex gap-1.5">
                      {p.colors.map(c => (
                        <span key={c} className="w-4 h-4 rounded-full border border-[#E5D9C8]" style={{ background: COLOR_HEX[c] }} title={t(`colors.${c}`)} />
                      ))}
                    </div>
                  )}
                  {buyer?.product_notes?.[p.id] && (
                    <p className="mt-3 text-xs px-2 py-1.5 bg-[#C5A059]/10 border-l-2 border-[#C5A059] text-[#3D2817]/80">
                      <span className="uppercase tracking-widest text-[10px] text-[#C5A059] mr-1">Your terms:</span>{buyer.product_notes[p.id]}
                    </p>
                  )}
                  <ReservedTerms terms={buyer?.product_terms?.[p.id]} compact />
                </div>
                <button
                  data-testid={`add-quote-${p.id}`}
                  onClick={() => add({ id: p.id, sku: p.sku, name: getPName(p, i18n.language) })}
                  disabled={inCart(p.id)}
                  className={`p-2 border transition-all ${
                    inCart(p.id) ? 'border-[#C5A059] bg-[#C5A059] text-white' : 'border-[#E5D9C8] hover:border-[#3D2817] hover:bg-[#3D2817] hover:text-white'
                  }`}
                  title={t('catalog.request_quote')}
                >
                  {inCart(p.id) ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
